import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormLabel,
    TextField,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { updateLead } from '../../redux/Slice/LeadSlice';
import { setLoading } from '../../redux/Slice/loaderSlice';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// Form validation schema with Yup
const validationSchema = Yup.object({ 
    name: Yup.string().required('Name is required'),
    designation: Yup.string().required('Designation is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
   phoneNumber: Yup.string().matches(/^\d{10}$/, 'Phone number must be 10 digits').required('Phone Number is required'),
});

const ManageContactPersons = ({ data, setData }) => {
    const [isAddingContact, setIsAddingContact] = useState(false);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const dispatch = useDispatch();
    const params = useParams();

    // Save or update an existing contact
    const handleSaveContact = async (values) => {
        const updatedContacts = [...data.contactPerson];
        if (editingContact) {
            // Update the contact
            const index = updatedContacts.findIndex(
                (contact) => contact._id === editingContact._id
            );
            updatedContacts[index] = { ...values };
        } else {
            // Add new contact
            updatedContacts.push({ ...values });
        }

        dispatch(setLoading(true));
        await dispatch(
            updateLead({
                l_id: params.id,
                formData: { ...data, contactPerson: updatedContacts },
            })
        );
        setData((prev) => ({ ...prev, contactPerson: updatedContacts }));
        setIsEditingContact(false);
        setIsAddingContact(false);
        setEditingContact(null);
        dispatch(setLoading(false));
    };

    // Start editing a contact
    const handleEditContact = (contact) => {
        setEditingContact(contact);
        setIsEditingContact(true);
        setIsAddingContact(false); // Make sure adding mode is off when editing
    };

    // Cancel editing or adding a contact
    const handleCancelEdit = () => {
        setIsAddingContact(false);
        setIsEditingContact(false);
        setEditingContact(null);
    };
    

    return (
        <Grid item size={12}>
            <Paper elevation={3} sx={{ p: 2, pb: 5 }}>
                <Box flexDirection="row" justifyContent="space-between" alignItems="center" display="flex">
                    <Typography variant="h6">Contact Persons</Typography>
                    <div>
                        <IconButton onClick={() => setIsAddingContact(true)}>
                            <Add />
                        </IconButton>
                    </div>
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Designation</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone Number</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.contactPerson?.map((person) => (
                                <TableRow key={person._id}>
                                    <TableCell>{person.name}</TableCell>
                                    <TableCell>{person.designation}</TableCell>
                                    <TableCell>{person.email}</TableCell>
                                    <TableCell>{person.phoneNumber}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEditContact(person)}>
                                            <Edit />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Add or Edit Contact Dialog */}
            <Dialog open={isAddingContact || isEditingContact} onClose={handleCancelEdit}>
                <DialogTitle>{isEditingContact ? 'Edit Contact Person' : 'Add Contact Person'}</DialogTitle>
                <DialogContent>
                    <Formik
                        initialValues={
                            isEditingContact
                                ? {
                                      name: editingContact.name,
                                      designation: editingContact.designation,
                                      email: editingContact.email,
                                      phoneNumber: editingContact.phoneNumber,
                                  }
                                : { name: '', designation: '', email: '', phoneNumber: '' }
                        }
                        validationSchema={validationSchema}
                        onSubmit={handleSaveContact}
                    >
                        {({ touched, errors }) => (
                            <Form>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <FormLabel>Name</FormLabel>
                                    <Field
                                        as={TextField}
                                        placeholder="Name"
                                        variant="outlined"
                                        fullWidth
                                        name="name"
                                        error={touched.name && !!errors.name}
                                        helperText={touched.name && errors.name}
                                    />
                                </FormControl>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <FormLabel>Designation</FormLabel>
                                    <Field
                                        as={TextField}
                                        placeholder="Designation"
                                        variant="outlined"
                                        fullWidth
                                        name="designation"
                                        error={touched.designation && !!errors.designation}
                                        helperText={touched.designation && errors.designation}
                                    />
                                </FormControl>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <FormLabel>Email</FormLabel>
                                    <Field
                                        as={TextField}
                                        placeholder="Email"
                                        variant="outlined"
                                        fullWidth
                                        name="email"
                                        error={touched.email && !!errors.email}
                                        helperText={touched.email && errors.email}
                                    />
                                </FormControl>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <FormLabel>Phone Number</FormLabel>
                                    <Field
                                        as={TextField}
                                        placeholder="Phone Number"
                                        variant="outlined"
                                        fullWidth
                                        name="phoneNumber"
                                        error={touched.phoneNumber && !!errors.phoneNumber}
                                        helperText={touched.phoneNumber && errors.phoneNumber}
                                    />
                                </FormControl>
                                <DialogActions>
                                    <Button type="submit" color="primary">
                                        {isEditingContact ? 'Save' : 'Add'}
                                    </Button>
                                    <Button onClick={handleCancelEdit} color="error">
                                        Cancel
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default ManageContactPersons;
